
import { AuthTokens, UserProfile } from './types';
import { NotificationService } from '../services/notification-service';
import { JwtValidator } from './jwt-validator';

// =================================================================
// === PATCH ANTI-REQUÊTES MULTIPLES: AJOUT DE DEBOUNCE ET CACHE ===
// =================================================================

// Configuration du limiteur d'appels (debounce)
const API_CALL_DEBOUNCE_MS = 3000; // Minimum 3 secondes entre appels identiques
const AUTH_CHECK_DEBOUNCE_MS = 1000; // Minimum 1 seconde entre vérifications d'authentification

// Mécanisme de cache avec timestamp par route
interface CacheEntry {
  data: any;
  timestamp: number;
  expiresAt: number;
}

// Cache global pour les appels API par URL
const apiCache: Record<string, CacheEntry> = {};

// Timestamp du dernier appel par méthode
const lastMethodCall: Record<string, number> = {
  checkAndRefreshToken: 0,
  isAuthenticated: 0,
  getRefreshToken: 0,
  getAccessToken: 0,
  getUserProfile: 0
};

// Vérifier si une méthode peut être exécutée en fonction de son délai min
function canExecuteMethod(methodName: string, debounceMs: number): boolean {
  const now = Date.now();
  const lastCall = lastMethodCall[methodName] || 0;
  
  // Si le délai minimum n'est pas écoulé, bloquer l'exécution
  if (now - lastCall < debounceMs) {
    console.log(`=== PATCH ANTI-SPAM: ${methodName} bloqué (appelé il y a ${now - lastCall}ms) ===`);
    return false;
  }
  
  // Mettre à jour le timestamp du dernier appel
  lastMethodCall[methodName] = now;
  return true;
}

// Fonction pour mettre en cache une réponse API
function cacheApiResponse(url: string, data: any, ttlMs: number = 30000): void {
  apiCache[url] = {
    data,
    timestamp: Date.now(),
    expiresAt: Date.now() + ttlMs
  };
  console.log(`=== PATCH CACHE: Réponse mise en cache pour ${url} (expire dans ${ttlMs/1000}s) ===`);
}

// Fonction pour récupérer une réponse en cache
function getCachedApiResponse(url: string): any | null {
  const entry = apiCache[url];
  if (!entry) return null;
  
  // Vérifier si l'entrée a expiré
  if (Date.now() > entry.expiresAt) {
    console.log(`=== PATCH CACHE: Entrée expirée pour ${url} ===`);
    delete apiCache[url];
    return null;
  }
  
  console.log(`=== PATCH CACHE: Utilisation du cache pour ${url} (âge: ${(Date.now() - entry.timestamp)/1000}s) ===`);
  return entry.data;
}
// === FIN DU PATCH ANTI-REQUÊTES MULTIPLES ===

// Cookie configuration
const AUTH_COOKIE_NAME = 'sentinel_auth_token';
const REFRESH_COOKIE_NAME = 'sentinel_refresh_token';
const PROFILE_COOKIE_NAME = 'sentinel_user_profile';
const SESSION_LAST_ACTIVITY = 'sentinel_last_activity';
const TOKEN_EXPIRY_MS = 60 * 60 * 1000; // 1 hour pour définir un cookie sécurisé
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes d'inactivité avant expiration de la session

// Fonction utilitaire pour définir un cookie sécurisé
function setCookie(name: string, value: string, expiryDays: number = 1): void {
  // PATCH: Always set auth cookie for cross-domain with correct attributes
  if (name === AUTH_COOKIE_NAME) {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + expiryDays);
    const expires = `expires=${expiryDate.toUTCString()}`;
    const cookieValue = `${name}=${encodeURIComponent(value)}; ${expires}; path=/; SameSite=None; Secure; domain=.cyber-threat-consulting.com`;
    document.cookie = cookieValue;
    console.log(`🔐 [PATCH] Auth cookie set with SameSite=None; Secure; domain=.cyber-threat-consulting.com:`, cookieValue);
    // Verification log
    setTimeout(() => {
      const cookies = document.cookie;
      const found = cookies.includes(`${name}=`);
      console.log(`🔐 [PATCH] Auth cookie presence after set:`, found ? 'PRESENT' : 'ABSENT', cookies);
    }, 100);
    return;
  }
  if (typeof document === 'undefined') return; // S'assurer qu'on est côté client
  
  console.log(`🔐 Début de stockage pour ${name}`);
  
  // IMPORTANT: Stocker d'abord dans localStorage comme méthode principale
  // pour garantir que les données sont toujours disponibles
  try {
    // Stocker directement avec le nom du cookie (sans préfixe)
    localStorage.setItem(name, value);
    console.log(`🔐 Valeur stockée dans localStorage pour ${name}`);
    
    // Également stocker avec le préfixe fallback_ pour la compatibilité
    localStorage.setItem(`fallback_${name}`, value);
  } catch (e) {
    console.warn(`🔐 Impossible de stocker dans localStorage pour ${name}:`, e);
  }
  
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + expiryDays);
  
  // Déterminer si nous sommes en production ou en développement
  const isProduction = process.env.NODE_ENV === 'production' || 
                     (typeof window !== 'undefined' && (
                       window.location.hostname === 'www.cyber-threat-consulting.com' ||
                       window.location.hostname === 'cyber-threat-consulting.com'
                     ));
  
  // Pour le token d'accès, utiliser des paramètres moins restrictifs
  const isAuthToken = name === AUTH_COOKIE_NAME;
  
  // Essayer plusieurs approches pour stocker le cookie
  try {
    // Approche 1: Cookie de base sans domaine spécifique
    const basicOptions = [
      `${name}=${encodeURIComponent(value)}`,
      `expires=${expiryDate.toUTCString()}`,
      'path=/',
      'SameSite=Lax'
    ];
    
    if (isProduction || (typeof location !== 'undefined' && location.protocol === 'https:')) {
      basicOptions.push('Secure');
    }
    
    const basicCookie = basicOptions.filter(Boolean).join('; ');
    console.log(`🔐 Setting cookie ${name} (basic):`, basicCookie);
    document.cookie = basicCookie;
    
    // Approche 2: Cookie avec SameSite=None pour les tokens d'accès (nécessite Secure)
    if (isAuthToken) {
      const sameSiteNoneOptions = [
        `${name}=${encodeURIComponent(value)}`,
        `expires=${expiryDate.toUTCString()}`,
        'path=/',
        'SameSite=None',
        'Secure'
      ];
      
      const sameSiteNoneCookie = sameSiteNoneOptions.filter(Boolean).join('; ');
      console.log(`🔐 Setting cookie ${name} (SameSite=None):`, sameSiteNoneCookie);
      document.cookie = sameSiteNoneCookie;
    }
    
    // Approche 3: Cookie avec domaine spécifique
    if (isProduction) {
      // Essayer plusieurs configurations de domaine
      const domainConfigs = [
        '.cyber-threat-consulting.com',  // Avec point pour inclure les sous-domaines
        'cyber-threat-consulting.com',   // Sans point
        'www.cyber-threat-consulting.com' // Domaine spécifique
      ];
      
      for (const domain of domainConfigs) {
        const domainOptions = [
          `${name}=${encodeURIComponent(value)}`,
          `expires=${expiryDate.toUTCString()}`,
          'path=/',
          'SameSite=None', // Utiliser None pour les cookies cross-domain
          'Secure',
          `domain=${domain}`
        ];
        
        const domainCookie = domainOptions.filter(Boolean).join('; ');
        console.log(`🔐 Setting cookie ${name} (with domain ${domain}):`, domainCookie);
        document.cookie = domainCookie;
      }
    }
  } catch (error) {
    console.error(`🔐 Error setting cookie ${name}:`, error);
  }
  
  // Vérifier immédiatement si le cookie a été défini
  setTimeout(() => {
    const storedCookie = getCookie(name);
    console.log(`🔐 Vérification finale pour ${name}:`, storedCookie ? 'présent' : 'manquant');
    
    // Vérifier également localStorage
    try {
      const localStorageValue = localStorage.getItem(name);
      console.log(`🔐 Vérification localStorage pour ${name}:`, localStorageValue ? 'présent' : 'manquant');
    } catch (e) {
      console.warn(`🔐 Erreur lors de la vérification localStorage:`, e);
    }
  }, 100);
}

// Fonction utilitaire pour obtenir un cookie
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null; // S'assurer qu'on est côté client
  
  // IMPORTANT: Vérifier d'abord dans localStorage comme source principale
  if (typeof localStorage !== 'undefined') {
    try {
      // Vérifier d'abord directement avec le nom (méthode principale)
      const directValue = localStorage.getItem(name);
      if (directValue) {
        console.log(`🔐 Valeur trouvée dans localStorage pour ${name}`);
        return directValue;
      }
      
      // Vérifier ensuite avec le préfixe fallback_ (méthode de secours)
      const fallbackValue = localStorage.getItem(`fallback_${name}`);
      if (fallbackValue) {
        console.log(`🔐 Valeur trouvée dans localStorage (fallback) pour ${name}`);
        return fallbackValue;
      }
    } catch (e) {
      console.warn(`🔐 Erreur lors de la lecture depuis localStorage pour ${name}:`, e);
    }
  }
  
  // Vérifier ensuite dans les cookies comme méthode secondaire
  try {
    const cookies = document.cookie.split('; ');
    const cookie = cookies.find(c => c.startsWith(`${name}=`));
    
    // Si trouvé, retourner la valeur et la stocker dans localStorage pour la prochaine fois
    if (cookie) {
      const value = decodeURIComponent(cookie.split('=')[1]);
      console.log(`🔐 Cookie ${name} trouvé dans document.cookie`);
      
      // Stocker dans localStorage pour la prochaine fois
      try {
        localStorage.setItem(name, value);
        localStorage.setItem(`fallback_${name}`, value);
        console.log(`🔐 Valeur du cookie synchronisée avec localStorage pour ${name}`);
      } catch (e) {
        console.warn(`🔐 Impossible de synchroniser avec localStorage:`, e);
      }
      
      return value;
    }
  } catch (e) {
    console.error(`🔐 Erreur lors de la lecture du cookie ${name}:`, e);
  }
  
  console.log(`🔐 Aucune valeur trouvée pour ${name} dans localStorage ou cookies`);
  return null;
}

// Fonction utilitaire pour supprimer un cookie
function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return; // S'assurer qu'on est côté client
  
  console.log(`🔐 Suppression du cookie ${name}`);
  
  // 1. Supprimer du localStorage (méthode principale)
  try {
    localStorage.removeItem(name);
    localStorage.removeItem(`fallback_${name}`);
    console.log(`🔐 Valeur supprimée de localStorage pour ${name}`);
  } catch (e) {
    console.warn(`🔐 Erreur lors de la suppression depuis localStorage pour ${name}:`, e);
  }
  
  // 2. Supprimer le cookie en définissant une date d'expiration dans le passé
  try {
    // Approche 1: Cookie de base
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
    
    // Approche 2: Cookie avec SameSite=None
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=None; Secure`;
    
    // Approche 3: Cookie avec domaines spécifiques
    const domainConfigs = [
      '.cyber-threat-consulting.com',
      'cyber-threat-consulting.com',
      'www.cyber-threat-consulting.com'
    ];
    
    for (const domain of domainConfigs) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}; SameSite=None; Secure`;
    }
    
    console.log(`🔐 Cookie ${name} supprimé`);
  } catch (error) {
    console.error(`🔐 Erreur lors de la suppression du cookie ${name}:`, error);
  }
}

// Intervalle de vérification du token (5 minutes)
const TOKEN_CHECK_INTERVAL_MS = 5 * 60 * 1000;

// Seuil de rafraîchissement (si moins de 10 minutes avant expiration)
const TOKEN_REFRESH_THRESHOLD_MS = 10 * 60 * 1000;

// Timer pour le rafraîchissement automatique
let tokenRefreshTimer: number | null = null;

export class TokenManager {
  // Initialiser le rafraîchissement automatique du token
  static initTokenRefresh(): void {
    // Arrêter le timer existant s'il y en a un
    if (tokenRefreshTimer !== null) {
      clearInterval(tokenRefreshTimer);
    }
    
    // Démarrer un nouveau timer
    tokenRefreshTimer = window.setInterval(() => {
      TokenManager.checkAndRefreshToken();
    }, TOKEN_CHECK_INTERVAL_MS);
    
    console.log('🔐 Rafraîchissement automatique du token initialisé, vérification toutes les', 
      TOKEN_CHECK_INTERVAL_MS / 60000, 'minutes');
    
    // Vérifier immédiatement au démarrage
    setTimeout(() => TokenManager.checkAndRefreshToken(), 1000);
  }
  
  // Arrêter le rafraîchissement automatique du token
  static stopTokenRefresh(): void {
    if (tokenRefreshTimer !== null) {
      clearInterval(tokenRefreshTimer);
      tokenRefreshTimer = null;
      console.log('🔐 Rafraîchissement automatique du token arrêté');
    }
  }
  
  /**
   * Synchroniser les tokens entre localStorage et cookies
   * Cette méthode assure que les tokens sont disponibles dans les deux emplacements de stockage
   */
  static synchronizeTokens(): void {
    try {
      // Liste des tokens à synchroniser
      const tokenNames = [
        AUTH_COOKIE_NAME,
        REFRESH_COOKIE_NAME,
        SESSION_LAST_ACTIVITY,
        PROFILE_COOKIE_NAME
      ];
      
      // Synchroniser chaque token
      for (const tokenName of tokenNames) {
        // Vérifier dans localStorage
        const localStorageValue = typeof window !== 'undefined' ? localStorage.getItem(tokenName) : null;
        
        // Vérifier dans les cookies
        const cookieValue = getCookie(tokenName);
        
        // Si la valeur existe dans localStorage mais pas dans les cookies
        if (localStorageValue && !cookieValue) {
          console.log(`🔐 Synchronisation: ${tokenName} trouvé dans localStorage mais pas dans les cookies`);
          setCookie(tokenName, localStorageValue, 30);
        }
        
        // Si la valeur existe dans les cookies mais pas dans localStorage
        if (cookieValue && !localStorageValue && typeof window !== 'undefined') {
          console.log(`🔐 Synchronisation: ${tokenName} trouvé dans les cookies mais pas dans localStorage`);
          localStorage.setItem(tokenName, cookieValue);
        }
      }
    } catch (error) {
      console.error('🔐 Erreur lors de la synchronisation des tokens:', error);
    }
  }
  
  // Vérifier et rafraîchir le token si nécessaire
  static async checkAndRefreshToken(): Promise<boolean> {
    // === PATCH ANTI-SPAM: Vérification du délai anti-rebond ===
    if (!canExecuteMethod('checkAndRefreshToken', API_CALL_DEBOUNCE_MS)) {
      // Retourner un succès (optimiste) pour éviter des erreurs en cascade
      return true;
    }
      
    try {
      // Vérifier si nous avons un token d'accès
      const accessToken = TokenManager.getAccessToken();
      if (!accessToken) {
        console.log('🔐 Aucun token d\'accès disponible, rafraîchissement non nécessaire');
        return false;
      }
      
      // Valider le token JWT
      const isValid = JwtValidator.validateToken(accessToken, {
        checkExpiration: true,
        checkNotBefore: true,
        requiredClaims: ['exp']
      });
      
      if (!isValid) {
        console.warn('🔐 Token JWT invalide');
        return false;
      }
      
      // Calculer le temps restant avant expiration
      const timeRemaining = JwtValidator.getTimeUntilExpiration(accessToken);
      if (timeRemaining === null) {
        console.warn('🔐 Impossible de déterminer le temps restant avant expiration');
        return false;
      }
      
      // Décoder le token pour obtenir la date d'expiration pour l'affichage
      const decodedToken = JwtValidator.decodeToken(accessToken);
      const expirationTime = decodedToken?.exp ? decodedToken.exp * 1000 : null;
      
      console.log('🔐 Vérification du token:', {
        expirationTime: expirationTime ? new Date(expirationTime).toLocaleString() : 'inconnue',
        tempsRestant: Math.floor(timeRemaining / 60000) + ' minutes',
        seuilRafraichissement: Math.floor(TOKEN_REFRESH_THRESHOLD_MS / 60000) + ' minutes'
      });
      
      // Si le token expire bientôt, le rafraîchir
      if (JwtValidator.isTokenAboutToExpire(accessToken, TOKEN_REFRESH_THRESHOLD_MS)) {
        console.log('🔐 Le token expire bientôt, tentative de rafraîchissement');
        
        // Vérifier si nous avons un refresh token
        const refreshToken = TokenManager.getRefreshToken();
        if (!refreshToken) {
          console.warn('🔐 Aucun refresh token disponible, impossible de rafraîchir le token');
          return false;
        }
        
        // Appeler l'API pour rafraîchir le token
        try {
          // Déterminer l'URL de base
          const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || 'https://www.cyber-threat-consulting.com';
          const apiUrl = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
          
          // Construire l'URL complète - tester plusieurs chemins possibles
          let refreshUrl = `${apiUrl}/token/refresh/`;
          
          // Définir les URLs alternatives à essayer en cas d'échec
          const alternativeUrls = [
            `${apiUrl}/auth/token/refresh/`,
            `${apiUrl}/accounts/auth/token/refresh/`,
            `${baseUrl}/api/auth/token/refresh/`
          ];
          
          console.log('🔐 Tentative de rafraîchissement du token à:', refreshUrl);
          
          // Effectuer la requête
          // Essayer d'abord l'URL principale
          let response = await fetch(refreshUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ refresh: refreshToken })
          });
          
          // Si la première URL échoue, essayer les alternatives
          if (!response.ok) {
            console.warn(`🔐 Échec avec l'URL principale ${refreshUrl} (${response.status}), essai des alternatives...`);
            
            // Essayer chaque URL alternative
            for (const altUrl of alternativeUrls) {
              try {
                console.log(`🔐 Tentative avec URL alternative: ${altUrl}`);
                response = await fetch(altUrl, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ refresh: refreshToken })
                });
                
                if (response.ok) {
                  console.log(`🔐 Succès avec l'URL alternative: ${altUrl}`);
                  // Mémoriser cette URL pour les prochaines tentatives
                  refreshUrl = altUrl;
                  // Si on a réussi, sortir de la boucle
                  break;
                } else {
                  console.warn(`🔐 Échec avec ${altUrl} (${response.status})`);
                }
              } catch (altError) {
                console.warn(`🔐 Erreur lors de la tentative avec ${altUrl}:`, altError);
              }
            }
          }
          
          // Vérifier si une des tentatives a réussi
          if (!response.ok) {
            console.error('🔐 Toutes les tentatives de rafraîchissement ont échoué. Dernière URL utilisée:', refreshUrl, 'Statut:', response.status);
            throw new Error(`Erreur HTTP: ${response.status} - Rafraîchissement impossible`);
          }
          
          const data = await response.json();
          console.log('🔐 Réponse de rafraîchissement:', data);
          
          if (data.access) {
            console.log('🔐 Token refreshed successfully with URL:', refreshUrl);
            
            // Calculer la nouvelle date d'expiration (par défaut 1 heure)
            const newExpiry = Date.now() + TOKEN_EXPIRY_MS;
            
            // Stocker le nouveau token
            TokenManager.setTokens({
              access_token: data.access,
              refresh_token: refreshToken, // Garder le même refresh token
              expires_at: newExpiry
            });
            
            // Notifier l'utilisateur que le token a été rafraîchî (de manière discrète)
            if (typeof window !== 'undefined' && window.document.hasFocus()) {
              // Afficher la notification uniquement si la fenêtre est active
              NotificationService.showTokenRefreshed();
            }
            
            return true;
          } else {
            console.warn('🔐 Réponse de rafraîchissement invalide:', data);
            NotificationService.showTokenError('Réponse de rafraîchissement invalide');
            return false;
          }
        } catch (error) {
          console.error('🔐 Erreur lors du rafraîchissement du token:', error);
          NotificationService.showTokenError(error);
          return false;
        }
      } else {
        console.log('🔐 Le token est encore valide pour', Math.floor(timeRemaining / 60000), 'minutes');
        return true;
      }
    } catch (error) {
      console.error('🔐 Erreur lors de la vérification du token:', error);
      return false;
    }
  }
  
  // Mettre à jour l'horodatage de la dernière activité de l'utilisateur
  static updateLastActivity(): void {
    try {
      const timestamp = Date.now().toString();
      // Vérifier si localStorage est disponible (côté client uniquement)
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(SESSION_LAST_ACTIVITY, timestamp);
      }
      setCookie(SESSION_LAST_ACTIVITY, timestamp, 1);
      console.log('🔐 Horodatage de la dernière activité mis à jour:', new Date(parseInt(timestamp)).toLocaleTimeString());
    } catch (e) {
      console.warn('🔐 Erreur lors de la mise à jour de l\'horodatage de la dernière activité:', e);
    }
  }
  
  // Récupérer l'horodatage de la dernière activité
  static getLastActivity(): string | null {
    // Vérifier d'abord dans les cookies
    const cookieValue = getCookie(SESSION_LAST_ACTIVITY);
    if (cookieValue) return cookieValue;
    
    // Ensuite vérifier dans localStorage
    if (typeof window !== 'undefined') {
      return localStorage.getItem(SESSION_LAST_ACTIVITY);
    }
    
    return null;
  }

  /**
   * Décode un token JWT pour extraire les informations utilisateur
   * @param token Le token JWT à décoder
   * @returns Les informations utilisateur contenues dans le token, ou null si le token est invalide
   */
  static decodeToken(token: string): any {
    try {
      // Utiliser le service de validation JWT pour décoder le token
      const decodedToken = JwtValidator.decodeToken(token);
      
      if (decodedToken) {
        console.log('🔐 Token décodé avec succès');
        
        // Afficher les informations d'administrateur dans le token pour débogage
        console.log('🔐 Informations d\'administrateur dans le token:', {
          is_staff: decodedToken.is_staff,
          is_superuser: decodedToken.is_superuser,
          role: decodedToken.role,
          groups: decodedToken.groups,
          permissions: decodedToken.permissions
        });
        
        // Normaliser les attributs d'administrateur
        // Certains backends peuvent utiliser des noms de champs différents
        if (decodedToken.is_staff === undefined && decodedToken.isStaff !== undefined) {
          decodedToken.is_staff = decodedToken.isStaff;
        }
        
        if (decodedToken.is_superuser === undefined && decodedToken.isSuperuser !== undefined) {
          decodedToken.is_superuser = decodedToken.isSuperuser;
        }
        
        // Certains backends peuvent inclure les attributs dans un sous-objet 'user'
        if (decodedToken.user && typeof decodedToken.user === 'object') {
          if (decodedToken.is_staff === undefined && decodedToken.user.is_staff !== undefined) {
            decodedToken.is_staff = decodedToken.user.is_staff;
          }
          
          if (decodedToken.is_superuser === undefined && decodedToken.user.is_superuser !== undefined) {
            decodedToken.is_superuser = decodedToken.user.is_superuser;
          }
        }
        
        // Inférer les attributs d'administrateur à partir du rôle si nécessaire
        if (decodedToken.is_staff === undefined && decodedToken.is_superuser === undefined) {
          const role = decodedToken.role || '';
          if (typeof role === 'string') {
            const lowerRole = role.toLowerCase();
            if (lowerRole === 'admin' || lowerRole === 'administrator' || lowerRole === 'superuser') {
              decodedToken.is_staff = true;
              decodedToken.is_superuser = true;
              console.log('🔐 Attributs d\'administrateur inférés à partir du rôle:', role);
            }
          }
        }
        
        // Inférer les attributs d'administrateur à partir des groupes si nécessaire
        if (decodedToken.is_staff === undefined && decodedToken.is_superuser === undefined && decodedToken.groups) {
          const groups = Array.isArray(decodedToken.groups) ? decodedToken.groups : [decodedToken.groups];
          const adminGroups = groups.some(g => {
            if (typeof g === 'string') {
              const lowerGroup = g.toLowerCase();
              return lowerGroup.includes('admin') || lowerGroup.includes('staff') || lowerGroup.includes('superuser');
            }
            return false;
          });
          
          if (adminGroups) {
            decodedToken.is_staff = true;
            decodedToken.is_superuser = true;
            console.log('🔐 Attributs d\'administrateur inférés à partir des groupes');
          }
        }
        
        // Afficher les informations d'administrateur après normalisation
        console.log('🔐 Informations d\'administrateur normalisées:', {
          is_staff: decodedToken.is_staff,
          is_superuser: decodedToken.is_superuser
        });
      } else {
        console.error('🔐 Erreur lors du décodage du token');
      }
      
      return decodedToken;
    } catch (error) {
      console.error('🔐 Exception lors du décodage du token:', error);
      return null;
    }
  }

  // Vérifier si la session a expiré en raison d'inactivité
  static hasSessionExpired(): boolean {
    try {
      // Vérifier d'abord si l'utilisateur a un token d'accès et un profil
      // S'il n'en a pas, il n'est pas connecté, donc pas de session à expirer
      const accessToken = this.getAccessToken();
      const userProfile = this.getUserProfile();
      
      if (!accessToken || !userProfile) {
        console.log('🔐 Aucun token ou profil trouvé, utilisateur non connecté');
        return false; // L'utilisateur n'est pas connecté, donc pas de session à expirer
      }
      
      // Vérifier d'abord dans les cookies
      let lastActivityStr = getCookie(SESSION_LAST_ACTIVITY);
      
      // Si pas trouvé dans les cookies, vérifier dans localStorage
      if (!lastActivityStr && typeof window !== 'undefined' && window.localStorage) {
        console.log('🔐 Vérification finale pour ' + SESSION_LAST_ACTIVITY + ': ' + (lastActivityStr ? '"présent"' : '"manquant"'));
        try {
          console.log('🔐 Vérification localStorage pour ' + SESSION_LAST_ACTIVITY + ': ' + (localStorage.getItem(SESSION_LAST_ACTIVITY) ? '"présent"' : '"manquant"'));
          lastActivityStr = localStorage.getItem(SESSION_LAST_ACTIVITY);
        } catch (e) {
          console.warn('🔐 Erreur lors de l\'accès à localStorage:', e);
        }
      }
      
      if (!lastActivityStr) {
        console.log('🔐 Aucun horodatage de dernière activité trouvé pour un utilisateur connecté, session considérée comme expirée');
        return true; // Si aucune activité n'est enregistrée pour un utilisateur connecté, considérer la session comme expirée
      }
      
      const lastActivityTime = parseInt(lastActivityStr);
      const currentTime = Date.now();
      const inactivityTime = currentTime - lastActivityTime;
      
      // Vérifier si le temps d'inactivité dépasse le délai d'expiration
      const hasExpired = inactivityTime > SESSION_TIMEOUT_MS;
      
      console.log('🔐 Vérification de l\'expiration de la session:', {
        dernièreActivité: new Date(lastActivityTime).toLocaleString(),
        tempsInactivité: `${Math.floor(inactivityTime / 60000)} minutes`,
        délaiExpiration: `${Math.floor(SESSION_TIMEOUT_MS / 60000)} minutes`,
        sessionExpirée: hasExpired
      });
      
      return hasExpired;
    } catch (e) {
      console.warn('🔐 Erreur lors de la vérification de l\'expiration de la session:', e);
      return false; // En cas d'erreur, ne pas considérer la session comme expirée si l'utilisateur n'est pas connecté
    }
  }
  
  // Définir les tokens dans un stockage sécurisé
  static setTokens(tokens: AuthTokens): void {
    console.log('🔐 TokenManager: Setting tokens', {
      access_token: tokens.access_token ? 'present' : 'missing',
      refresh_token: tokens.refresh_token ? 'present' : 'missing',
      expires_at: tokens.expires_at
    });
    
    // 0. Initialiser ou mettre à jour l'horodatage de la dernière activité
    this.updateLastActivity();
    
    // 1. Stocker le token d'accès
    if (tokens.access_token) {
      // Calculer la durée d'expiration en jours
      const expiryDays = tokens.expires_at 
        ? (tokens.expires_at - Date.now()) / (1000 * 60 * 60 * 24)
        : 1; // Par défaut, 1 jour
      
      // Stocker dans localStorage en priorité
      localStorage.setItem(AUTH_COOKIE_NAME, tokens.access_token);
      
      // Puis dans les cookies
      setCookie(AUTH_COOKIE_NAME, tokens.access_token, expiryDays);
    }
    
    // 2. Stocker le token de rafraîchissement (plus longue durée)
    if (tokens.refresh_token) {
      // Stocker dans localStorage en priorité
      localStorage.setItem(REFRESH_COOKIE_NAME, tokens.refresh_token);
      
      // Puis dans les cookies
      setCookie(REFRESH_COOKIE_NAME, tokens.refresh_token, 30); // Expire après 30 jours
    }
  }
  
  // Récupérer le token d'accès
  static getAccessToken(): string | null {
    // Vérifier d'abord dans localStorage
    if (typeof window !== 'undefined') {
      const localStorageToken = localStorage.getItem(AUTH_COOKIE_NAME);
      if (localStorageToken) {
        console.log('🔐 Valeur trouvée dans localStorage pour ' + AUTH_COOKIE_NAME);
        return localStorageToken;
      }
    }
    
    // Ensuite vérifier dans les cookies
    const cookieToken = getCookie(AUTH_COOKIE_NAME);
    if (cookieToken) {
      console.log('🔐 Valeur trouvée dans cookie pour ' + AUTH_COOKIE_NAME);
      return cookieToken;
    }
    
    return null;
  }
  
  // Récupérer le token de rafraîchissement
  static getRefreshToken(): string | null {
    // Vérifier d'abord dans localStorage
    if (typeof window !== 'undefined') {
      const localStorageToken = localStorage.getItem(REFRESH_COOKIE_NAME);
      if (localStorageToken) {
        console.log('🔐 Valeur trouvée dans localStorage pour ' + REFRESH_COOKIE_NAME);
        return localStorageToken;
      }
    }
    
    // Ensuite vérifier dans les cookies
    const cookieToken = getCookie(REFRESH_COOKIE_NAME);
    if (cookieToken) {
      console.log('🔐 Valeur trouvée dans cookie pour ' + REFRESH_COOKIE_NAME);
      return cookieToken;
    }
    
    return null;
  }
  
  // Stocker le profil utilisateur
  static setUserProfile(profile: UserProfile): void {
    try {
      console.log('🔐 Début du stockage du profil utilisateur:', profile);
      
      // Vérifier que le profil est valide
      if (!profile || typeof profile !== 'object') {
        console.error('🔐 Profil utilisateur invalide:', profile);
        return;
      }
      
      // S'assurer que les champs obligatoires sont présents
      const validatedProfile = {
        ...profile,
        id: profile.id || 0,
        email: profile.email || '',
        username: profile.username || '',
        role: profile.role || 'user',
        is_active: profile.is_active !== undefined ? profile.is_active : true
      };
      
      // Stocker sous forme de chaîne JSON
      const profileJson = JSON.stringify(validatedProfile);
      console.log('🔐 Profil utilisateur JSON:', profileJson);
      
      // Stocker dans le cookie
      setCookie(PROFILE_COOKIE_NAME, profileJson, 30);
      
      // Stocker également dans localStorage pour une meilleure disponibilité
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(PROFILE_COOKIE_NAME, profileJson);
          // Stocker également sous une clé de secours pour la compatibilité
          localStorage.setItem(`fallback_${PROFILE_COOKIE_NAME}`, profileJson);
          console.log('🔐 Profil utilisateur stocké dans localStorage');
        } catch (storageError) {
          console.error('🔐 Erreur lors du stockage dans localStorage:', storageError);
        }
      }
      
      // Vérifier que le profil a bien été stocké
      const storedProfile = this.getUserProfile();
      console.log('🔐 Vérification du profil stocké:', storedProfile);
      
    } catch (error) {
      console.error('🔐 Erreur lors du stockage du profil utilisateur:', error);
    }
  }

  // Récupérer le profil utilisateur
  static getUserProfile(): UserProfile | null {
    try {
      console.log('🔐 Début de récupération du profil utilisateur');
      
      // Essayer d'abord de récupérer depuis le cookie
      let profileStr = getCookie(PROFILE_COOKIE_NAME);
      console.log('🔐 Profil depuis cookie:', profileStr ? 'trouvé' : 'non trouvé');
      
      // Si non trouvé dans le cookie, essayer localStorage directement
      if (!profileStr && typeof localStorage !== 'undefined') {
        profileStr = localStorage.getItem(PROFILE_COOKIE_NAME);
        console.log('🔐 Profil depuis localStorage:', profileStr ? 'trouvé' : 'non trouvé');
      }
      
      // Si toujours pas trouvé, essayer avec le préfixe fallback_
      if (!profileStr && typeof localStorage !== 'undefined') {
        profileStr = localStorage.getItem(`fallback_${PROFILE_COOKIE_NAME}`);
        console.log('🔐 Profil depuis fallback localStorage:', profileStr ? 'trouvé' : 'non trouvé');
      }
      
      // Si aucun profil n'est trouvé, essayer de le récupérer à partir du token JWT
      if (!profileStr) {
        const accessToken = this.getAccessToken();
        if (accessToken) {
          console.log('🔐 Tentative de récupération du profil depuis le token JWT');
          const decodedToken = this.decodeToken(accessToken);
          
          if (decodedToken) {
            // Créer un profil utilisateur à partir des informations du token
            const userProfile: UserProfile = {
              id: decodedToken.user_id || decodedToken.sub || 0,
              email: decodedToken.email || '',
              username: decodedToken.username || decodedToken.preferred_username || decodedToken.name || decodedToken.email || 'Utilisateur',
              role: decodedToken.role || 'user',
              is_active: true
            };
            
            console.log('🔐 Profil créé à partir du token JWT:', userProfile);
            
            // Stocker ce profil pour une utilisation future
            this.setUserProfile(userProfile);
            
            return userProfile;
          }
        }
      }
      
      if (!profileStr) {
        console.log('🔐 Aucun profil utilisateur trouvé');
        return null;
      }
      
      // Parser le profil utilisateur
      try {
        const profile = JSON.parse(profileStr) as UserProfile;
        console.log('🔐 Profil utilisateur récupéré:', profile);
        return profile;
      } catch (parseError) {
        console.error('🔐 Erreur lors du parsing du profil utilisateur:', parseError);
        return null;
      }
    } catch (error) {
      console.error('🔐 Erreur lors de la récupération du profil utilisateur:', error);
      return null;
    }
  }
  
  // Clear all authentication data with enhanced security
  static clearTokens(): void {
    try {
      console.log('🔐 TokenManager: Début du processus de suppression des données d\'authentification');
      
      // 1. Supprimer tous les cookies d'authentification
      deleteCookie(AUTH_COOKIE_NAME);
      deleteCookie(REFRESH_COOKIE_NAME);
      deleteCookie(PROFILE_COOKIE_NAME);
      deleteCookie(SESSION_LAST_ACTIVITY);
      
      // 2. Supprimer également les données du localStorage par sécurité
      // Même si nous préférons ne pas utiliser localStorage, nous le nettoyons
      // pour éviter toute fuite de données d'authentification
      try {
        if (typeof localStorage !== 'undefined') {
          // Supprimer les clés directes (ajoutées dans notre modification de setCookie)
          localStorage.removeItem(AUTH_COOKIE_NAME);
          localStorage.removeItem(REFRESH_COOKIE_NAME);
          localStorage.removeItem(PROFILE_COOKIE_NAME);
          localStorage.removeItem(SESSION_LAST_ACTIVITY);
          
          // Supprimer les clés avec préfixe fallback_
          localStorage.removeItem(`fallback_${AUTH_COOKIE_NAME}`);
          localStorage.removeItem(`fallback_${REFRESH_COOKIE_NAME}`);
          localStorage.removeItem(`fallback_${PROFILE_COOKIE_NAME}`);
          localStorage.removeItem(`fallback_${SESSION_LAST_ACTIVITY}`);
          
          // Supprimer les autres clés potentielles
          localStorage.removeItem('user_profile');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('sentinel_user');
          localStorage.removeItem('sentinel_token');
          
          // Rechercher et supprimer toutes les clés liées à l'authentification
          const keysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.includes('token') || key.includes('auth') || 
                      key.includes('user') || key.includes('profile') || 
                      key.includes('sentinel'))) {
              keysToRemove.push(key);
            }
          }
          
          // Supprimer les clés identifiées
          keysToRemove.forEach(key => {
            localStorage.removeItem(key);
            console.log(`🔐 Clé localStorage supprimée: ${key}`);
          });
        }
      } catch (e) {
        console.warn('🔐 Erreur lors du nettoyage du localStorage:', e);
      }
      
      console.log('🔐 Toutes les données d\'authentification ont été supprimées');
    } catch (e) {
      console.error('🔐 Erreur critique lors de la suppression des données d\'authentification:', e);
    }
  }
  
  // Cache pour le résultat d'authentification
  private static lastAuthResult: boolean | null = null;
  private static lastAuthCheck: number = 0;
  
  // Vérifier si l'utilisateur est authentifié
  static isAuthenticated(): boolean {
    // === PATCH ANTI-SPAM: Vérification de fréquence d'appel ===
    const now = Date.now();
    
    // Si on a vérifié récemment, retourner le résultat en cache
    if (now - this.lastAuthCheck < AUTH_CHECK_DEBOUNCE_MS && this.lastAuthResult !== null) {
      console.log(`=== PATCH ANTI-SPAM: isAuthenticated() utilisé depuis le cache (${Math.floor((now - this.lastAuthCheck))}ms) ===`);
      return this.lastAuthResult;
    }
    
    // Limiter la fréquence d'appel à cette méthode
    if (!canExecuteMethod('isAuthenticated', AUTH_CHECK_DEBOUNCE_MS)) {
      // Si nous avons déjà un résultat en cache, l'utiliser
      if (this.lastAuthResult !== null) {
        return this.lastAuthResult;
      }
    }
    
    // Mettre à jour le timestamp de la dernière vérification
    this.lastAuthCheck = now;
    
    try {
      console.log('🔐 TokenManager: Vérification de l\'authentification avec sécurité renforcée');
      
      // Synchroniser les tokens entre localStorage et cookies
      this.synchronizeTokens();
      
      // Vérifier d'abord si la session a expiré (seulement si l'utilisateur est connecté)
      if (this.hasSessionExpired()) {
        console.log('🔐 Session expirée, déconnexion automatique');
        this.clearTokens();
        
        // Afficher une notification de session expirée
        NotificationService.showSessionExpired();
        
        // Rediriger vers la page de connexion avec un message
        if (typeof window !== 'undefined') {
          // Ajouter un délai pour permettre à l'utilisateur de voir la notification
          setTimeout(() => {
            window.location.href = `/login?session_expired=${Date.now()}`;
          }, 1500);
        }
        
        this.lastAuthResult = false;
        return false;
      }
      
      // Mettre à jour l'horodatage de la dernière activité
      this.updateLastActivity();
      
      // Vérifier ensuite si nous avons un token d'accès valide
      const accessToken = this.getAccessToken();
      const refreshToken = this.getRefreshToken();
      const userProfile = this.getUserProfile();
      
      // Vérifier la validité du token JWT si présent
      let tokenValid = false;
      let tokenExpired = false;
      
      if (accessToken) {
        // Valider le token JWT
        tokenValid = JwtValidator.validateToken(accessToken, {
          checkExpiration: true,
          checkNotBefore: true
        });
        
        // Vérifier si le token est expiré
        const timeRemaining = JwtValidator.getTimeUntilExpiration(accessToken);
        tokenExpired = timeRemaining !== null && timeRemaining <= 0;
        
        // Si le token est invalide mais que nous avons un refresh token, essayer de le rafraîchir
        if ((!tokenValid || tokenExpired) && refreshToken) {
          console.log('🔐 Token invalide ou expiré, programmation d\'un rafraîchissement automatique');
          // Programmer un rafraîchissement automatique
          setTimeout(() => this.checkAndRefreshToken(), 100);
        }
      }
      
      // IMPORTANT: Si nous avons un token de rafraîchissement mais pas de token d'accès,
      // essayer de rafraîchir le token immédiatement
      if (!accessToken && refreshToken) {
        console.log('🔐 Token d\'accès manquant mais refresh token présent, rafraîchissement immédiat');
        this.checkAndRefreshToken();
      }
      
      // Considérer l'utilisateur comme authentifié si nous avons un token valide et un profil utilisateur
      // OU si nous avons un refresh token et un profil utilisateur (même si le token d'accès est expiré)
      const isAuthenticated = (!!accessToken && tokenValid && !!userProfile) || 
                             (!!refreshToken && !!userProfile);
      
      console.log('🔐 TokenManager: Résultat de la vérification d\'authentification', {
        accessToken: accessToken ? 'present' : 'absent',
        refreshToken: refreshToken ? 'present' : 'absent',
        userProfile: userProfile ? 'present' : 'absent',
        tokenValid: accessToken ? (tokenValid ? 'Oui' : 'Non') : 'N/A',
        tokenExpired: accessToken ? (tokenExpired ? 'Oui' : 'Non') : 'N/A',
        isAuthenticated
      });
      
      // === PATCH: Mettre en cache le résultat pour les prochains appels ===
      this.lastAuthResult = isAuthenticated;
      
      return isAuthenticated;
    } catch (error) {
      console.error('🔐 Erreur lors de la vérification de l\'authentification:', error);
      return false;
    }
  }
}
